/**
 * @file movements-texts.ts
 * @description Реестр операций журнала движений и подписи строк
 * (phase19.24 этап 1, шаг 2). Применение отбора к окну — `movements-filter.ts`.
 *
 * Зачем реестр: в базе 11 видов движения (`StockMovementKind`), а кладовщик
 * мыслит семью операциями — «приёмка», «продажа», «резерв», «снятие резерва»,
 * «списание», «перемещение», «корректировка». Пара `TRANSFER_IN` +
 * `TRANSFER_OUT` — это ОДНА операция «перемещение», а те же два вида с
 * комментарием скрипта бэкфилла — вообще не перемещение, а технический перенос
 * остатка на партию (см. правки ревью phase19.24 этап 1, шаг 1 и TD-56).
 *
 * Поэтому операция описана данными: подпись, серверный `kind` (если операция
 * ложится ровно на один вид — тогда фильтрует сервер) и предикат отбора для
 * клиентской доводки. Новая операция = запись в `MOVEMENT_OPERATION_OPTIONS`,
 * а не ветка `if` в панели (OCP, docs/processes/engineering-principles.md).
 *
 * Подписи строк тоже здесь: журнал, карточка на мобильном и таблица обязаны
 * называть одно движение одинаково.
 */

import { MOVEMENT_KIND_LABEL, type StockMovementKind } from "./api-wms";

/**
 * Префикс комментария разового скрипта `backfill-technical-lots.ts`.
 *
 * Скрипт чинил уже проведённые приёмки: переносил беспартийный остаток на
 * техническую партию парой `TRANSFER_OUT`/`TRANSFER_IN` (`ADJUST` сервисом не
 * проводится — TD-56). В журнале эти пары выглядели как «Перемещение (вход)» и
 * «Перемещение (выход)» по одному и тому же складу — кладовщик читал это как
 * сбой. Подписываем их отдельно.
 *
 * Источник строки — `apps/api/prisma/scripts/backfill-technical-lots.ts`: там
 * этот префикс пишется в `notes` движения. Менять его можно только вместе со
 * скриптом, иначе подпись молча перестанет находиться.
 */
export const LOT_BACKFILL_NOTE_PREFIX = "backfill-technical-lots";

/** Минимум полей движения, по которым считается операция и подпись. */
export interface MovementLike {
  kind: StockMovementKind;
  notes: string | null;
}

/** Значение селекта «Операция»; `all` — без отбора. */
export type MovementOperation =
  | "all"
  | "receive"
  | "sale"
  | "reserve"
  | "unreserve"
  | "writeoff"
  | "transfer"
  | "adjust";

/** Описание операции: подпись, серверный фильтр и предикат отбора. */
export interface MovementOperationMeta {
  value: MovementOperation;
  label: string;
  /**
   * Вид для query-параметра `kind`. Задан только там, где операция = ровно
   * один вид: сервер отдаёт уже отфильтрованное окно, и лимит 500 тратится на
   * нужные строки. Операции из нескольких видов фильтруются на клиенте —
   * сервер принимает один `kind`, а не список.
   */
  serverKind?: StockMovementKind;
  /** Попадает ли движение в операцию (работает и поверх серверного отбора). */
  match: (movement: MovementLike) => boolean;
}

/** Движение — технический перенос остатка на партию, а не работа склада. */
export function isLotBackfillMovement(movement: MovementLike): boolean {
  return (movement.notes ?? "").startsWith(LOT_BACKFILL_NOTE_PREFIX);
}

/** Вид движения — половинка перемещения (вход или выход). */
function isTransferKind(kind: StockMovementKind): boolean {
  return kind === "TRANSFER_IN" || kind === "TRANSFER_OUT";
}

/** Отбор по умолчанию: без сужения по виду движения. */
const ALL_OPERATIONS: MovementOperationMeta = {
  value: "all",
  label: "Все операции",
  match: () => true,
};

/** Реестр операций селекта — порядок реестра = порядок в списке. */
export const MOVEMENT_OPERATION_OPTIONS: MovementOperationMeta[] = [
  ALL_OPERATIONS,
  {
    value: "receive",
    label: "Приёмка",
    serverKind: "RECEIVE",
    match: (m) => m.kind === "RECEIVE",
  },
  {
    value: "sale",
    label: "Продажа",
    serverKind: "SALE",
    match: (m) => m.kind === "SALE",
  },
  {
    value: "reserve",
    label: "Резерв",
    serverKind: "RESERVE",
    match: (m) => m.kind === "RESERVE",
  },
  {
    value: "unreserve",
    label: "Снятие резерва",
    serverKind: "UNRESERVE",
    match: (m) => m.kind === "UNRESERVE",
  },
  {
    value: "writeoff",
    label: "Списание",
    serverKind: "WRITEOFF",
    match: (m) => m.kind === "WRITEOFF",
  },
  {
    value: "transfer",
    label: "Перемещение",
    // Два вида (вход и выход) — серверного `kind` быть не может; заодно
    // выбрасываем технические пары бэкфилла, у них своя операция ниже.
    match: (m) => isTransferKind(m.kind) && !isLotBackfillMovement(m),
  },
  {
    value: "adjust",
    label: "Корректировка",
    // Честные `ADJUST` (их пока не проводит сервис — TD-56) и переносы на
    // техническую партию: для кладовщика это одна корзина «правка остатка».
    match: (m) => m.kind === "ADJUST" || isLotBackfillMovement(m),
  },
];

/** Быстрый доступ к записи реестра; неизвестное значение — «все операции». */
function operationMeta(operation: MovementOperation): MovementOperationMeta {
  return (
    MOVEMENT_OPERATION_OPTIONS.find((item) => item.value === operation) ??
    ALL_OPERATIONS
  );
}

/** Значение `?op=` из URL; всё неизвестное — «все операции». */
export function parseMovementOperation(raw: string | null): MovementOperation {
  const found = MOVEMENT_OPERATION_OPTIONS.find((item) => item.value === raw);
  return found ? found.value : "all";
}

/** Серверный `kind` для query; `undefined` — фильтруем окно на клиенте. */
export function movementOperationServerKind(
  operation: MovementOperation,
): StockMovementKind | undefined {
  return operationMeta(operation).serverKind;
}

/** Отбор строки по выбранной операции (клиентская доводка окна). */
export function matchesMovementOperation(
  movement: MovementLike,
  operation: MovementOperation,
): boolean {
  return operationMeta(operation).match(movement);
}

/** Подпись операции в строке: «Приёмка», «Корректировка партии». */
export function movementOperationText(movement: MovementLike): string {
  if (isLotBackfillMovement(movement)) return "Корректировка партии";
  return MOVEMENT_KIND_LABEL[movement.kind];
}

/**
 * Пояснение под номером документа: комментарий движения как есть, а для
 * технического переноса — человеческий текст вместо служебной строки скрипта.
 */
export function movementNoteText(movement: MovementLike): string | null {
  if (isLotBackfillMovement(movement)) {
    return "перенос остатка на техническую партию";
  }
  const notes = movement.notes?.trim();
  return notes ? notes : null;
}
